import moment from "moment-timezone";

/**
 * Convert a date/time to organization timezone
 * @param {Date|string} dateTime - The date/time to convert
 * @param {string} orgTimeZone - Organization timezone (e.g., "America/Los_Angeles")
 * @returns {moment.Moment} - Moment object in organization timezone
 */
export function toOrgTimezone(dateTime, orgTimeZone) {
  return moment.tz(dateTime, orgTimeZone);
}

/**
 * Convert UTC time to organization timezone
 * @param {Date|string} utcDateTime - UTC date/time
 * @param {string} orgTimeZone - Organization timezone
 * @returns {moment.Moment} - Moment object in organization timezone
 */
export function utcToOrgTimezone(utcDateTime, orgTimeZone) {
  return moment.utc(utcDateTime).tz(orgTimeZone);
}

/**
 * Get current time in organization timezone
 * @param {string} orgTimeZone - Organization timezone
 * @returns {moment.Moment} - Current moment in organization timezone
 */
export function nowInOrgTimezone(orgTimeZone) {
  return moment.tz(orgTimeZone);
}

/**
 * Get start of today in organization timezone
 * @param {string} orgTimeZone - Organization timezone
 * @returns {Date} - Start of today as Date object for date pickers
 */
export function todayInOrgTimezone(orgTimeZone) {
  return moment.tz(orgTimeZone).startOf('day').toDate();
}

/**
 * Format date/time for display in organization timezone
 * @param {Date|string} dateTime - Date/time to format
 * @param {string} orgTimeZone - Organization timezone
 * @param {string} format - Moment format string (default: "DD/MM/YYYY HH:mm")
 * @returns {string} - Formatted date/time string
 */
export function formatInOrgTimezone(dateTime, orgTimeZone, format = "DD/MM/YYYY HH:mm") {
  return utcToOrgTimezone(dateTime, orgTimeZone).format(format);
}

/**
 * Check if two dates are the same day in organization timezone
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @param {string} orgTimeZone - Organization timezone
 * @returns {boolean} - True if same day in org timezone
 */
export function isSameDayInOrgTimezone(date1, date2, orgTimeZone) {
  const moment1 = toOrgTimezone(date1, orgTimeZone);
  const moment2 = toOrgTimezone(date2, orgTimeZone);
  return moment1.isSame(moment2, 'day');
}

/**
 * Check if a booking time is in the future relative to organization timezone
 * @param {Date|string} endTime - Booking end time
 * @param {string} orgTimeZone - Organization timezone
 * @returns {boolean} - True if booking is in the future
 */
export function isFutureBookingInOrgTimezone(endTime, orgTimeZone) {
  const now = nowInOrgTimezone(orgTimeZone);
  const bookingEnd = utcToOrgTimezone(endTime, orgTimeZone);
  return bookingEnd.isAfter(now);
}

/**
 * Check if a date is in the past relative to organization timezone
 * @param {Date|string} date - Date to check
 * @param {string} orgTimeZone - Organization timezone
 * @returns {boolean} - True if date is in the past
 */
export function isDateInPastInOrgTimezone(date, orgTimeZone) {
  const now = nowInOrgTimezone(orgTimeZone);
  const dateInOrgTz = toOrgTimezone(date, orgTimeZone);
  return dateInOrgTz.isBefore(now);
}

/**
 * Create a date/time string in YYYY-MM-DD format for API calls
 * @param {Date} date - Date object to format
 * @param {string} orgTimeZone - Organization timezone
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export function formatDateForAPI(date, orgTimeZone) {
  return toOrgTimezone(date, orgTimeZone).format("YYYY-MM-DD");
} 